import React, { useState } from 'react';
import { useCartStorage } from '../../hooks/useCartStorage';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Alert, Container, Grid, Divider } from '@mui/material';
import ShoppingCart from '../../components/ShoppingCart';

const PlaceOrderLipoderma = ({ currentUser, onOrderSubmit }) => {
  const navigate = useNavigate();
  
  const [quantity, setQuantity] = useState(1);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartItems, setCartItems] = useCartStorage();

  // Lipoderma product data
  const product = {
    name: 'Lipoderma™',
    sku: 'LIP-001',
    productId: 'LIP-001',
    clientPrice: 600.00,
    listPrice: 650.00,
    discount: 50.00,
    rating: 5,
    reviewCount: 12,
    images: ['/UnisynHub/Assets/lipoderma-card.png'],
    sizing: '10 mL Vial',
    shipping: 'Cryopreserved, shipped Next-Day on dry ice.',
    description: 'Lipoderma™ is an advanced adipose-derived regenerative tissue therapy for aesthetic medicine. Premium solution for facial volume restoration and soft tissue augmentation.',
    features: [
      'Adipose-derived tissue matrix',
      'Superior volume restoration',
      'Natural-looking results',
      'Minimally invasive procedure'
    ],
    category: 'Human Aesthetics',
    productType: 'lipoderma'
  };

  const primaryColor = '#8B7355'; // Lipoderma brown/tan

  const handleQuantityChange = (change) => {
    setQuantity(Math.max(1, quantity + change));
  };

  const handleAddToCart = () => {
    const newItem = {
      name: product.name,
      price: product.clientPrice,
      quantity: quantity,
      image: product.images[0],
      sku: product.sku,
      productId: product.productId || product.sku
    };

    const existingItemIndex = cartItems.findIndex(item => item.sku === product.sku);
    
    if (existingItemIndex >= 0) {
      const updatedCartItems = [...cartItems];
      updatedCartItems[existingItemIndex].quantity += quantity;
      setCartItems(updatedCartItems);
    } else {
      setCartItems([...cartItems, newItem]);
    }
    
    setCartOpen(true);
    setQuantity(1);
  };

  const handleUpdateCartQuantity = (itemIndex, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveFromCart(itemIndex);
      return;
    }
    
    const updatedCartItems = [...cartItems];
    updatedCartItems[itemIndex].quantity = newQuantity;
    setCartItems(updatedCartItems);
  };

  const handleRemoveFromCart = (itemIndex) => {
    const updatedCartItems = cartItems.filter((_, index) => index !== itemIndex);
    setCartItems(updatedCartItems);
  };

  const handleCheckout = () => {
    setCartOpen(false);
    setCartItems([]);
    navigate('/client/checkout', {
      state: { cartItems }
    });
  };

  return (
    <Box sx={{ backgroundColor: '#ffffff', fontFamily: 'Poppins, sans-serif', minHeight: '100vh' }}>
      {/* Cart Summary Bar */}
      {cartItems.length > 0 && (
        <Box sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          backgroundColor: primaryColor,
          color: 'white',
          py: 2,
          px: 3,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <Typography sx={{ fontWeight: 600 }}>
            {cartItems.reduce((total, item) => total + item.quantity, 0)} item(s) in cart
          </Typography>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Typography sx={{ fontWeight: 600 }}>
              Total: ${cartItems.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2)}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setCartOpen(true)}
              sx={{ bgcolor: 'white', color: primaryColor, fontWeight: 600 }}
            >
              View Cart
            </Button>
            <Button
              variant="contained"
              onClick={handleCheckout}
              sx={{ bgcolor: '#1a5a1a', color: 'white', fontWeight: 600 }}
            >
              Checkout
            </Button>
          </Box>
        </Box>
      )}

      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="success" sx={{ mb: 3 }}>
          <Typography sx={{ fontWeight: 600 }}>Client Exclusive Pricing Active</Typography>
          <Typography>You're saving ${product.discount} per vial</Typography>
        </Alert>

        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <Box sx={{ 
              width: '100%',
              height: '560px',
              backgroundColor: '#f5f5f5',
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img 
                src={product.images[0]}
                alt={product.name}
                style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
              />
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              {product.name}
            </Typography>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h5" sx={{ fontWeight: 600, color: primaryColor }}>
                ${product.clientPrice.toFixed(2)}
              </Typography>
              <Typography variant="h6" sx={{ color: '#999', textDecoration: 'line-through' }}>
                ${product.listPrice.toFixed(2)}
              </Typography>
              <Box sx={{ px: 2, py: 0.5, bgcolor: '#ecfdf5', borderRadius: '12px' }}>
                <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#059669' }}>
                  Save ${product.discount.toFixed(2)}
                </Typography>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            <Typography variant="body1" paragraph>
              {product.description}
            </Typography>

            {product.features.map((feature, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Box sx={{ width: 18, height: 18, backgroundColor: primaryColor, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography sx={{ color: 'white', fontSize: '12px' }}>✓</Typography>
                </Box>
                <Typography>{feature}</Typography>
              </Box>
            ))}

            <Box sx={{ mt: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid #e6e6e6', borderRadius: '24px', padding: '8px' }}>
                <Button 
                  onClick={() => handleQuantityChange(-1)}
                  sx={{ minWidth: 32, width: 32, height: 32, borderRadius: '50%', bgcolor: '#f2f2f2' }}
                >
                  -
                </Button>
                <Typography sx={{ width: '60px', textAlign: 'center', fontWeight: 500 }}>
                  {quantity}
                </Typography>
                <Button 
                  onClick={() => handleQuantityChange(1)}
                  sx={{ minWidth: 32, width: 32, height: 32, borderRadius: '50%', bgcolor: '#f2f2f2' }}
                >
                  +
                </Button>
              </Box>

              <Button
                onClick={handleAddToCart}
                variant="contained"
                sx={{
                  flex: 1,
                  height: 48,
                  backgroundColor: primaryColor,
                  color: 'white',
                  fontSize: '16px',
                  fontWeight: 600,
                  borderRadius: '24px',
                  '&:hover': { opacity: 0.9 }
                }}
              >
                Add to Cart • ${(product.clientPrice * quantity).toFixed(2)}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Container>

      <ShoppingCart
        isOpen={cartOpen}
        onClose={() => setCartOpen(false)}
        cartItems={cartItems}
        onUpdateQuantity={handleUpdateCartQuantity}
        onRemoveItem={handleRemoveFromCart}
        onCheckout={handleCheckout}
      />
    </Box>
  );
};

export default PlaceOrderLipoderma;
